import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/hooks/use-language';
import { usePageHero } from '@/hooks/usePageHero';
import { ArrowUpRight, X, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { SEO } from '@/components/ui/SEO';
import { buildBreadcrumbs } from '@/lib/schema';
import { DateRangeCalendar } from '@/components/news/DateRangeCalendar';
import { getPagePath, type Lang } from '@/lib/localizedRoutes';
import { Pagination } from '@/components/ui/pagination';
import { useIsMobile } from '@/hooks/use-mobile';
import { useScrollLock } from '@/hooks/use-scroll-lock';
import { PublicNewsItem } from '@/lib/dataSource';
import { getCachedNews, fetchNews, subscribeToCache } from '@/lib/globalDataCache';

interface NewsItem {
  id: number;
  title: string;
  category: string;
  dateISO: string;
  excerpt: string;
  content: string;
  imageUrl?: string;
  invalidDate?: boolean;
}

export const News: React.FC<{ seoEnabled?: boolean }> = ({ seoEnabled = true }) => {
  const { t, language } = useLanguage();
  const newsHero = usePageHero({
    pageKey: 'news',
    lang: language,
    fallbacks: {
      eyebrow: t('page.news.official'),
      title: t('page.news.title'),
    },
  });
  const isMobile = useIsMobile();
  const { category: categorySlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  
  // Animation state management
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayedCategory, setDisplayedCategory] = useState(categorySlug || 'all');
  const [showContent, setShowContent] = useState(true);
  const pendingCategoryRef = useRef<string | null>(null);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Date range filter state
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState<Date | null>(null);
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const PAGE_SIZE = 20;

  // Detect if the device is mobile or tablet for responsive styling
  const formatDate = (dateISO: string, lang: string) => {
    if (!dateISO) return '';
    const date = new Date(dateISO);
    if (isNaN(date.getTime())) return '';
    
    const day = date.getDate();
    const monthIdx = date.getMonth();
    const year = date.getFullYear();

    const months = {
      ru: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
      en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      uz: ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr']
    };

    if (lang === 'ru') {
      return `${day.toString().padStart(2, '0')} ${months.ru[monthIdx]} ${year}`;
    }
    if (lang === 'uz') {
      return `${year}-yil ${day} ${months.uz[monthIdx]}`;
    }
    return `${months.en[monthIdx]} ${day}, ${year}`;
  };

  // Body scroll lock
  useScrollLock(!!selectedNews);

  useEffect(() => {
    // Basic overflow lock for mobile/tablet only (under 1025px)
    if (isMobile) {
      if (selectedNews) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedNews, isMobile]);

  // ESC key to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedNews(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  // Handle category change with animation sequence
  const handleCategoryChange = useCallback((newCategory: string) => {
    const targetCategory = newCategory || 'all';
    
    // If already showing this category, do nothing
    if (targetCategory === displayedCategory && !isAnimating) {
      return;
    }
    
    // If animating, queue this as the pending category
    if (isAnimating) {
      pendingCategoryRef.current = targetCategory;
      return;
    }
    
    // Start animation sequence
    setIsAnimating(true);
    setShowContent(false); // Trigger fade out
    
    // Clear any existing timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    
    // After fade out completes, switch category and fade in
    animationTimeoutRef.current = setTimeout(() => {
      // Check if there's a pending category (from rapid clicks)
      const finalCategory = pendingCategoryRef.current || targetCategory;
      pendingCategoryRef.current = null;
      
      setDisplayedCategory(finalCategory);
      setShowContent(true); // Trigger fade in
      
      // Mark animation as complete after fade in
      animationTimeoutRef.current = setTimeout(() => {
        setIsAnimating(false);
        
        // Check if there's another pending category
        if (pendingCategoryRef.current) {
          const nextCategory = pendingCategoryRef.current;
          pendingCategoryRef.current = null;
          handleCategoryChange(nextCategory);
        }
      }, 400); // Wait for fade in to complete
    }, 250); // Wait for fade out to complete
  }, [displayedCategory, isAnimating]);

  // Sync with URL changes
  useEffect(() => {
    const newCategory = categorySlug || 'all';
    if (newCategory !== displayedCategory) {
      handleCategoryChange(newCategory);
    }
  }, [categorySlug, handleCategoryChange]);

  const categoryMap = useMemo(() => [
    { slug: 'all', key: 'news.categories.all', path: '/news' },
    { slug: 'factory-events', key: 'news.categories.plant_events', path: '/news/factory-events' },
    { slug: 'press', key: 'news.categories.press', path: '/news/press' },
    { slug: 'experts', key: 'news.categories.expert_advice', path: '/news/experts' },
    { slug: 'openings-gallery', key: 'news.categories.opening_gallery', path: '/news/openings-gallery' },
  ], []);

  // Нормализатор: DB category slug → URL slug (для фильтрации)
  // DB хранит: factory-events, press, expert-advice, discovery-gallery
  // URL использует: factory-events, press, experts, openings-gallery
  const normalizeCategory = useCallback((dbCategory: string): string => {
    switch (dbCategory) {
      case 'expert-advice':      return 'experts';
      case 'discovery-gallery':  return 'openings-gallery';
      default:                   return dbCategory;
    }
  }, []);

  const currentCategory = categoryMap.find(c => c.slug === (categorySlug || 'all')) || categoryMap[0];
  const activeCategoryName = t(currentCategory.key);

  const headerVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };

  // ─── DB news — uses global cache for instant render ─────────────────────────
  //
  // IMPORTANT: each localized slug (`novosti`/`yangiliklar`/`news`) is a separate
  // <Route>, so a language switch causes React Router to fully unmount and
  // remount this page. On remount, plain `useRef([])` / `useState(null)` would
  // start empty, the first paint would render an empty grid, and only the
  // async `fetchNews()` resolution would later re-render with cards. That
  // visible blank-grid → cards transition is exactly what users perceive as
  // "dёрганье / flicker" of the news list when switching language.
  //
  // The fix: initialize BOTH refs synchronously from the global news cache so
  // the very first render after remount already has localized cards.

  const mapItems = useCallback((items: PublicNewsItem[], lang: string): NewsItem[] => {
    return items.map((item, idx) => ({
      id: 10000 + idx,
      dateISO: item.date,
      category: normalizeCategory(item.category || 'factory-events'),
      title: item.title[lang as keyof typeof item.title] || item.title.ru || '',
      imageUrl: item.coverImage || undefined,
      excerpt: item.excerpt[lang as keyof typeof item.excerpt] || item.excerpt.ru || '',
      content: item.body[lang as keyof typeof item.body] || item.body.ru || '',
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Synchronous bootstrap from global cache — no empty-grid frame on remount.
  const rawNewsRef = React.useRef<PublicNewsItem[]>(getCachedNews() ?? []);
  const [dbNews, setDbNews] = useState<NewsItem[] | null>(() => {
    const cached = getCachedNews();
    return cached && cached.length > 0 ? mapItems(cached, language) : null;
  });

  // When language changes WITHIN the same mount (e.g. Header click on a
  // route that didn't change slug), remap synchronously during render.
  const lastNewsLangRef = useRef(language);
  if (lastNewsLangRef.current !== language) {
    lastNewsLangRef.current = language;
    if (rawNewsRef.current.length > 0) {
      setDbNews(mapItems(rawNewsRef.current, language));
    } else {
      const cached = getCachedNews();
      if (cached && cached.length > 0) {
        rawNewsRef.current = cached;
        setDbNews(mapItems(cached, language));
      }
    }
  }

  useEffect(() => {
    fetchNews().then((items: PublicNewsItem[]) => {
      rawNewsRef.current = items;
      setDbNews(mapItems(items, language));
    });

    const unsub = subscribeToCache(() => {
      const updated = getCachedNews();
      if (updated) {
        rawNewsRef.current = updated;
        setDbNews(mapItems(updated, language));
      }
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // null = грузится → пустой список (не показываем старую статику)
  // [] = DB пустая или ошибка
  // [...] = есть данные из DB
  const news: NewsItem[] = dbNews ?? [];

  const processedNews = useMemo(() => {
    return news
      .filter(item => {
        if (!item.dateISO) return false;
        const date = new Date(item.dateISO);
        return !isNaN(date.getTime());
      })
      .sort((a, b) => {
        return b.dateISO.localeCompare(a.dateISO);
      });
  }, [news]);

  // Use displayedCategory for filtering (not categorySlug directly)
  const filteredNews = useMemo(() => {
    let filtered = processedNews;
    
    // Filter by category
    if (displayedCategory && displayedCategory !== 'all') {
      filtered = filtered.filter(n => n.category === displayedCategory);
    }
    
    // Filter by date range
    if (dateRangeStart && dateRangeEnd) {
      filtered = filtered.filter(n => {
        const newsDate = new Date(n.dateISO);
        // Normalize dates to start of day for comparison
        const start = new Date(dateRangeStart);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRangeEnd);
        end.setHours(23, 59, 59, 999);
        return newsDate >= start && newsDate <= end;
      });
    }
    
    return filtered;
  }, [processedNews, displayedCategory, dateRangeStart, dateRangeEnd]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredNews.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginatedNews = filteredNews.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [displayedCategory, dateRangeStart, dateRangeEnd]);

  // Reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Handle page change with smooth scroll
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    
    // Smooth scroll to grid container
    if (gridContainerRef.current) {
      const headerOffset = 150; // Account for sticky header and filter bar
      const elementPosition = gridContainerRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  const handleDateRangeApply = useCallback((start: Date | null, end: Date | null) => {
    setDateRangeStart(start);
    setDateRangeEnd(end);
  }, []);

  const handleDateRangeReset = useCallback(() => {
    setDateRangeStart(null);
    setDateRangeEnd(null);
  }, []);

  const seoTitle = categorySlug && categorySlug !== 'all'
    ? `${t('page.news.title')} — ${activeCategoryName} | SARGO`
    : (language === 'uz'
        ? 'SARGO yangiliklari | Ishlab chiqarish, yangiliklar va materiallar'
        : language === 'en'
          ? 'SARGO News | Production, Updates and Materials'
          : 'Новости SARGO | Производство, обновления и материалы');

  const newsPath = getPagePath('news', language as Lang);
  const currentPath = categorySlug ? `${newsPath}/${categorySlug}` : newsPath;
  const isNewsPage = location.pathname.startsWith(newsPath);

  return (
    <div>
      <SEO 
        enabled={seoEnabled}
        title={seoTitle}
        description={language === 'uz'
          ? "SARGO yangiliklari: ishlab chiqarish voqealari, kompaniya yangiliklari, soha materiallari va ekspert ma'lumotlari."
          : language === 'en'
            ? 'SARGO news: production events, company updates, industry materials and expert information.'
            : 'Новости SARGO: производственные события, обновления компании, отраслевые материалы и экспертная информация.'}
        canonical={currentPath}
        schema={buildBreadcrumbs(window.location.origin, [
          { name: language === 'uz' ? 'Bosh sahifa' : language === 'en' ? 'Home' : 'Главная', url: `/${language}` },
          { name: language === 'uz' ? 'Yangiliklar' : language === 'en' ? 'News' : 'Новости', url: newsPath },
        ])}
      />
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-12 lg:pb-16 relative">
        <div className="grid-cells opacity-50" />
        <div className="container-custom relative z-10">
          <div className="space-y-4 lg:space-y-6">
            {/*
              Hero header. Each localized slug (`novosti`/`yangiliklar`/`news`)
              is a separate <Route>, so a language switch fully remounts this
              page. We render hero elements WITHOUT the initial="hidden" fade
              (i.e. `initial={false}`) so the user does not see the page fade
              through opacity 0 / x:-30 on every language change.
            */}
            {newsHero.eyebrow?.trim() && (
              <motion.div
                custom={0}
                initial={false}
                animate="visible"
                variants={headerVariants}
                className="flex items-center gap-2 lg:gap-3"
              >
                <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
                <p className="text-primary font-black uppercase tracking-[0.4em] text-[10px]">{newsHero.eyebrow}</p>
              </motion.div>
            )}
            {newsHero.title?.trim() && (
              <motion.h1
                custom={1}
                initial={false}
                animate="visible"
                variants={headerVariants}
                className="font-black uppercase tracking-tighter leading-[0.95] lg:leading-[0.8]"
              >
                {newsHero.title}<span className="text-primary">.</span>
              </motion.h1>
            )}
            {newsHero.subtitle?.trim() && (
              <motion.p
                custom={2}
                initial={false}
                animate="visible"
                variants={headerVariants}
                className="text-white/40 max-w-xl text-sm md:text-base lg:text-lg leading-relaxed font-black uppercase tracking-wider"
              >
                {newsHero.subtitle}
              </motion.p>
            )}
          </div>
        </div>
      </section>

      {/* Sticky Filter Bar */}
      <div className="hidden lg:block sticky top-[60px] lg:top-[72px] z-30 bg-[#13100B]/95 backdrop-blur-md border-y border-white/5 py-4">
        <div className="container-custom">
          <div key={language} className="flex items-center gap-6 overflow-x-auto no-scrollbar scroll-smooth">
            {categoryMap.map((cat) => {
              const isActive = (categorySlug || 'all') === cat.slug;
              return (
                <button
                  key={cat.slug}
                  onClick={() => navigate(cat.path)}
                  className={cn(
                    "whitespace-nowrap text-[10px] md:text-xs font-black uppercase tracking-[0.2em] transition-all relative py-2 px-1",
                    isActive ? "text-primary" : "text-white/30 hover:text-white"
                  )}
                >
                  {t(cat.key)}
                  {isActive && (
                    <motion.div 
                      layoutId="news-tab-underline"
                      className="absolute bottom-0 left-0 w-full h-[2px] bg-primary shadow-[0_0_10px_rgba(176,138,90,0.5)]"
                    />
                  )}
                </button>
              );
            })}

            {/* Desktop Date Filter Button */}
            <div className="ml-auto flex items-center gap-4">
              <div className="h-4 w-[1px] bg-white/10" />
              <button
                onClick={() => setIsCalendarOpen(true)}
                className={cn(
                  "flex items-center gap-2 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] transition-all py-2 px-3 border border-white/5 rounded-sm",
                  dateRangeStart && dateRangeEnd 
                    ? "text-primary border-primary/30 bg-primary/5 shadow-[0_0_15px_rgba(176,138,90,0.1)]" 
                    : "text-white/30 hover:text-white hover:border-white/10"
                )}
              >
                <Calendar size={14} className={dateRangeStart && dateRangeEnd ? "text-primary" : "text-white/30"} />
                {dateRangeStart && dateRangeEnd 
                  ? `${dateRangeStart.toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-US', { day: '2-digit', month: '2-digit' })} – ${dateRangeEnd.toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-US', { day: '2-digit', month: '2-digit' })}`
                  : t('news.filter_date')
                }
                {dateRangeStart && dateRangeEnd && (
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateRangeReset();
                    }}
                    className="ml-1 p-0.5 hover:bg-primary/20 rounded-full transition-colors"
                  >
                    <X size={12} />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* News Grid */}
      <section className="py-12 lg:py-20 relative">
        <div className="grid-cells-accent opacity-10" />
        <div className="container-custom relative z-10">
          {/* No loading skeleton — data comes from global cache instantly */}

          <motion.div 
            ref={gridContainerRef}
            className={cn("grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12")}
            initial={false}
            animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : -10 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            {paginatedNews.length > 0 ? (
              paginatedNews.map((item, index) => (
                // initial={false} — when News is remounted on language switch
                // (each localized slug is its own <Route>), we don't want every
                // card to fade in from opacity:0 / y:30 again. Cards appear
                // immediately in their final state. The category-change
                // fade-out/fade-in via `showContent` still works because
                // `animate` reads the current showContent value.
                <motion.article
                  key={`${displayedCategory}-${item.id}`}
                  initial={false}
                  animate={{
                    opacity: showContent ? 1 : 0,
                    y: showContent ? 0 : 30,
                  }}
                  transition={{
                    duration: 0.4,
                    delay: showContent ? index * 0.1 : 0, // Staggered delay for each card
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                  onClick={() => setSelectedNews(item)}
                  className="group space-y-6 lg:space-y-8 cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden mirror-card">
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                      />
                    )}
                    <div className="absolute top-4 left-4 lg:top-6 lg:left-6 flex flex-col gap-2">
                      <div className="bg-background/90 backdrop-blur-md border border-white/10 px-3 lg:px-4 py-1 text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-primary w-fit">
                        {t(categoryMap.find(c => c.slug === item.category)?.key || '')}
                      </div>
                      <div className="bg-background/90 backdrop-blur-md border border-white/10 px-3 lg:px-4 py-1 text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-white/50 w-fit">
                        {formatDate(item.dateISO, language)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3 lg:space-y-4 max-w-2xl">
                    <h3 className="text-xl lg:text-2xl font-bold uppercase tracking-tight group-hover:text-primary transition-colors leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-white/40 text-xs lg:text-sm leading-relaxed line-clamp-3">
                      {item.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-primary group-hover:gap-4 transition-all duration-300 pt-2 min-h-[44px]">
                      {t('news.read_fully')} <ArrowUpRight size={14} />
                    </div>
                  </div>
                </motion.article>
              ))
            ) : (
              <motion.div
                key={`empty-${displayedCategory}`}
                initial={false}
                animate={{
                  opacity: showContent ? 1 : 0,
                  y: showContent ? 0 : 20,
                }}
                transition={{
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                className="col-span-full py-20 text-center"
              >
                <p className="text-white/20 font-black uppercase tracking-[0.4em] text-xs lg:text-sm">
                  {t('news.empty') || 'Материалов пока нет'}
                </p>
              </motion.div>
            )}
          </motion.div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 lg:mt-16">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </section>

      {/* News Modal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {selectedNews && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6 lg:p-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedNews(null)}
                className="absolute inset-0 bg-[#13100B]/85 backdrop-blur-xl"
              />
              
              <motion.div
                initial={{ opacity: 0, scale: 0.98, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 10 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className={cn(
                  "relative bg-background border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col mirror-style",
                  isMobile 
                    ? "w-[92%] h-[70vh] rounded-[18px] overflow-hidden" 
                    : "w-full max-w-4xl max-h-[calc(100vh-80px)] md:rounded-none overflow-y-auto overscroll-contain"
                )}
              >
                {/* Close Button - Sticky/Fixed relative to modal */}
                <button 
                  onClick={() => setSelectedNews(null)}
                  className={cn(
                    "absolute top-4 right-4 z-[70] flex items-center justify-center bg-background/50 backdrop-blur-md border border-white/10 text-white transition-all duration-300",
                    isMobile ? "w-10 h-10 rounded-full" : "w-10 h-10 hover:bg-primary rounded-sm"
                  )}
                >
                  <X size={20} />
                </button>

                <div className={cn(
                  "flex-1 custom-scrollbar no-scrollbar flex flex-col",
                  isMobile ? "overflow-y-auto" : "overflow-visible"
                )}>
                  {/* Modal Header/Image */}
                  <div className={cn(
                    "relative overflow-hidden flex-shrink-0",
                    isMobile ? "aspect-video" : "aspect-[21/9]"
                  )}>
                    {selectedNews.imageUrl && (
                      <img
                        src={selectedNews.imageUrl}
                        alt={selectedNews.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    )}
                    
                    {!isMobile && (
                      <div className="absolute bottom-6 left-6 lg:bottom-10 lg:left-10 space-y-4">
                        <div className="flex flex-wrap gap-2">
                          <div className="bg-primary px-3 py-1 text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-white">
                            {t(categoryMap.find(c => c.slug === selectedNews.category)?.key || '')}
                          </div>
                          <div className="bg-white/10 backdrop-blur-md border border-white/10 px-3 py-1 text-[9px] lg:text-[10px] font-bold uppercase tracking-widest text-white/70">
                            {formatDate(selectedNews.dateISO, language)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Modal Content */}
                  <div className={cn(
                    "space-y-6 lg:space-y-8 bg-background/40 backdrop-blur-sm flex-1",
                    isMobile ? "p-6" : "p-6 lg:p-12 pt-4 lg:pt-8"
                  )}>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_10px_rgba(176,138,90,0.8)]"></div>
                        <div className="text-primary text-[9px] font-black uppercase tracking-[0.3em]">
                          {t(categoryMap.find(c => c.slug === selectedNews.category)?.key || '')} • {formatDate(selectedNews.dateISO, language)}
                        </div>
                      </div>
                      <h2 className={cn(
                        "font-black uppercase tracking-tight leading-tight",
                        isMobile ? "text-xl" : "text-2xl lg:text-4xl"
                      )}>
                        {selectedNews.title}
                      </h2>
                    </div>
                    
                    <div className="space-y-3">
                       <div className="text-[9px] uppercase tracking-widest font-bold text-white/20">{t('portfolio.modal.description_label')}</div>
                       <div className="space-y-6">
                        {selectedNews.content.split('\n').map((paragraph, idx) => (
                          <p 
                            key={idx} 
                            className={cn(
                              "text-white/40 leading-relaxed font-medium",
                              isMobile ? "text-[11px]" : "text-sm lg:text-base"
                            )}
                          >
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {!isMobile && <div className="h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />}
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Date Range Calendar Modal */}
      {isNewsPage && (
        <DateRangeCalendar
          isOpen={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
          onApply={handleDateRangeApply}
          onReset={handleDateRangeReset}
          initialStartDate={dateRangeStart}
          initialEndDate={dateRangeEnd}
        />
      )}
    </div>
  );
};
